// ─────────────────────────────────────────────────────────────
// Tileset Kenney « Sketch Desert » (CC0) — registre, maths iso,
// et boîtes englobantes (pour recadrer les vignettes proprement :
// c'est ce qui évite les « quarts de palmier » dans la boutique).
// ─────────────────────────────────────────────────────────────

export const BASE = "/kenney-desert";

// Géométrie fournie par Kenney (map_tiles.tsx : 232×110, offset -12/+6)
export const ISO_W = 232;
export const ISO_H = 110;
export const IMG_W = 256;
export const IMG_H = 352;

// Grille : parcelles de CH tuiles, séparées par GAP tuiles d'eau
// (les parcelles ne se touchent jamais — elles sont reliées par des ponts).
export const CH = 6;
export const GAP = 2;
export const STRIDE = CH + GAP;

// Bornes de zoom, partagées par le canvas et les boutons de l'interface.
// MAX est passé de 0,95 à 4 : on peut maintenant vraiment s'approcher
// d'un bâtiment pour le caler au pixel près.
export const ZOOM_MIN = 0.05;
export const ZOOM_MAX = 4;

export const chunkOrigin = (cx, cy) => ({ ox: cx * STRIDE, oy: cy * STRIDE });

export function isoScreen(gx, gy) {
  return { sx: (gx - gy) * (ISO_W / 2), sy: (gx + gy) * (ISO_H / 2) };
}
export function screenToTile(sx, sy) {
  const gx = (sx / (ISO_W / 2) + sy / (ISO_H / 2)) / 2;
  const gy = (sy / (ISO_H / 2) - sx / (ISO_W / 2)) / 2;
  return { tx: Math.round(gx), ty: Math.round(gy) };
}

// ─────────────────────────────────────────────────────────────
// MODÈLE EN BLOCS
//
// Ce pack fonctionne comme Minecraft : chaque image EST un bloc qui
// occupe exactement une case. Deux familles :
//
//   BLOC PLEIN (isCube)  le sprite contient son propre cube de terre.
//                        Il REMPLACE le bloc de sol de la case.
//   ACCESSOIRE           pas de cube : l'objet se POSE sur le sol,
//                        qui reste dessiné dessous.
// ─────────────────────────────────────────────────────────────
const ANCHOR_X = 127.5;
const DEFAULT_ANCHOR_Y = 182.5;

// Hauteur d'un cube, mesurée sur les sprites : face supérieure à 182,5,
// base à 291. Un bloc EMPILÉ sur le sol est remonté de cette valeur.
export const CUBE_H = 108.5;

// Un sprite mal calé ? Ajuste sa valeur ici : + descend, − remonte.
// Un pas de 110 correspond à une tuile en diagonale.
export const TILE_ANCHOR = {
  "building_center_S": 182.5,
  "building_dark_center_S": 182.5,
  "building_dark_center_door_S": 182.5,
  "building_dark_center_windows_S": 182.5,
  "building_dark_sides_S": 182.5,
  "building_dark_sides_door_S": 182.5,
  "building_dark_sides_windows_S": 182.5,
  "building_sides_S": 182.5,
  "dirt_center_S": 182.5,
  "dirt_low_S": 182.5,
  "dome_S": 292.6,
  "dome_small_S": 292.8,
  "grass_center_S": 182.5,
  "grass_corner_S": 182.5,
  "grass_river_S": 182.5,
  "grass_waterConcave_S": 182.5,
  "grass_waterConvex_S": 182.5,
  "grass_water_S": 182.5,
  "overhang_S": 214.3,
  "overhang_small_S": 227.6,
  "rocks_S": 297.6,
  "stairs_full_S": 182.5,
  "stairs_left_S": 278.0,
  "stairs_right_S": 305.0,
  "structure_tentSlant_S": 182.5,
  "structure_tent_S": 182.5,
  "tiles_S": 182.5,
  "tiles_crumbled_S": 182.5,
  "tiles_decorated_S": 182.5,
  "tiles_steps_S": 182.5,
  "tree_S": 291.8,
  "trees_S": 263.3,
  "walls_broken_S": 294.3,
  "walls_corner_S": 182.5,
  "walls_end_S": 182.5,
  "walls_left_S": 269.4,
  "walls_right_S": 316.4,
  "walls_sides_S": 182.5,
  "walls_square_S": 182.5,
  "water_center_S": 182.5,
  "water_fall_S": 182.5,
};

export const TILE_IS_CUBE = {
  "building_center_S": true,
  "building_dark_center_S": true,
  "building_dark_center_door_S": true,
  "building_dark_center_windows_S": true,
  "building_dark_sides_S": true,
  "building_dark_sides_door_S": true,
  "building_dark_sides_windows_S": true,
  "building_sides_S": true,
  "dirt_center_S": true,
  "dirt_low_S": true,
  "dome_S": false,
  "dome_small_S": false,
  "grass_center_S": true,
  "grass_corner_S": true,
  "grass_river_S": true,
  "grass_waterConcave_S": true,
  "grass_waterConvex_S": true,
  "grass_water_S": true,
  "overhang_S": false,
  "overhang_small_S": false,
  "rocks_S": false,
  "stairs_full_S": true,
  "stairs_left_S": false,
  "stairs_right_S": false,
  "structure_tentSlant_S": true,
  "structure_tent_S": true,
  "tiles_S": true,
  "tiles_crumbled_S": true,
  "tiles_decorated_S": true,
  "tiles_steps_S": true,
  "tree_S": false,
  "trees_S": false,
  "walls_broken_S": false,
  "walls_corner_S": true,
  "walls_end_S": true,
  "walls_left_S": false,
  "walls_right_S": false,
  "walls_sides_S": true,
  "walls_square_S": true,
  "water_center_S": true,
  "water_fall_S": true,
};

const anchorOf = (file) => TILE_ANCHOR[file] ?? DEFAULT_ANCHOR_Y;

export const isCubeTile = (tileKey) => !!TILE_IS_CUBE[TILE_FILES[tileKey]];

// Position de dessin d'un bloc.
// tileKeyOrAnchor :
//   - une clé de TILE_FILES (ex. "dome")  → calage du pack Kenney
//   - un nombre                            → calage explicite, utilisé
//     par les images uploadées par l'admin (voir overrides.js)
// mul : facteur d'agrandissement de l'image elle-même. Vaut 1 pour un
//   bloc d'une case ; pour une image qui remplace une structure de
//   plusieurs cases (une grande médersa en un seul dessin), il vaut la
//   largeur de l'emprise. L'ANCRE est multipliée elle aussi — sans ça
//   l'image « glisserait » vers le bas dès qu'on l'agrandit.
export function drawPos(sx, sy, scale = 1, tileKeyOrAnchor = null, mul = 1) {
  const anchorY = typeof tileKeyOrAnchor === "number"
    ? tileKeyOrAnchor
    : anchorOf(tileKeyOrAnchor ? TILE_FILES[tileKeyOrAnchor] : null);
  const s = scale * mul;
  return {
    dx: sx - ANCHOR_X * s,
    dy: sy - anchorY * s,
    dw: IMG_W * s,
    dh: IMG_H * s,
  };
}

// ── Palette exacte du pack, échantillonnée sur les PNG ────────
export const PAL = {
  ink: "#80472f",
  inkDeep: "#5e3323",
  sand: "#f8d9b3",
  sandMid: "#e0c4a2",
  sandDark: "#c7ae8f",
  terra: "#f8956d",
  terraMid: "#e08762",
  terraDark: "#c77857",
  clayDark: "#935237",
  leaf: "#a3d258",
  leafMid: "#93bd4f",
  leafDark: "#688b30",
  water: "#b6eeff",
  waterMid: "#7fc9e4",
  waterDeep: "#3f7f9c",
  lampWarm: "#ffcb6b",
  lampCore: "#fff3cf",
};

export const TILE_FILES = {
  dirt: "dirt_center_S",
  dirtLow: "dirt_low_S",
  grass: "grass_center_S",
  grassCorner: "grass_corner_S",
  rocks: "rocks_S",
  tree: "tree_S",
  trees: "trees_S",
  house: "building_center_S",
  houseDoor: "building_dark_center_door_S",
  houseWindows: "building_dark_center_windows_S",
  houseSide: "building_sides_S",
  houseDarkSide: "building_dark_sides_S",
  dome: "dome_S",
  domeSmall: "dome_small_S",
  tent: "structure_tent_S",
  tentSlant: "structure_tentSlant_S",
  wallSquare: "walls_square_S",
  wallSides: "walls_sides_S",
  wallCorner: "walls_corner_S",
  wallEnd: "walls_end_S",
  wallLeft: "walls_left_S",
  wallRight: "walls_right_S",
  wallBroken: "walls_broken_S",
  floorTiles: "tiles_S",
  floorDecorated: "tiles_decorated_S",
  floorCrumbled: "tiles_crumbled_S",
  floorSteps: "tiles_steps_S",
  stairsFull: "stairs_full_S",
  stairsLeft: "stairs_left_S",
  stairsRight: "stairs_right_S",
  overhang: "overhang_S",
  overhangSmall: "overhang_small_S",
  waterCenter: "water_center_S",
  waterFall: "water_fall_S",
};

// Types de sol peignables dans l'éditeur de terrain
export const GROUND_TYPES = [
  { id: "dirt", label: "Terre", tile: "dirt" },
  { id: "grass", label: "Herbe", tile: "grass" },
  { id: "stone", label: "Dalles", tile: "floorTiles" },
  { id: "decorated", label: "Dalles ornées", tile: "floorDecorated" },
  { id: "crumbled", label: "Ruines", tile: "floorCrumbled" },
];

// Boîtes englobantes [x0,y0,x1,y1] dans l'image 256×352
export const TILE_BBOX = {
  "building_center_S": [11,120,244,349],
  "building_dark_center_S": [11,128,244,349],
  "building_dark_center_door_S": [11,128,244,349],
  "building_dark_center_windows_S": [11,128,244,350],
  "building_dark_sides_S": [11,128,244,349],
  "building_dark_sides_door_S": [11,128,244,349],
  "building_dark_sides_windows_S": [11,128,244,350],
  "building_sides_S": [11,120,244,349],
  "dirt_center_S": [11,128,244,348],
  "dirt_low_S": [11,155,243,349],
  "dome_S": [42,196,212,335],
  "dome_small_S": [66,218,189,324],
  "grass_center_S": [11,128,244,348],
  "grass_corner_S": [11,128,244,320],
  "grass_river_S": [11,128,244,349],
  "grass_waterConcave_S": [11,142,244,348],
  "grass_waterConvex_S": [11,142,244,349],
  "grass_water_S": [11,142,244,349],
  "overhang_S": [37,148,244,265],
  "overhang_small_S": [80,168,244,265],
  "rocks_S": [58,242,187,329],
  "stairs_full_S": [11,132,244,348],
  "stairs_left_S": [66,155,244,321],
  "stairs_right_S": [12,159,191,349],
  "structure_tentSlant_S": [12,172,243,349],
  "structure_tent_S": [12,130,243,348],
  "tiles_S": [12,128,244,349],
  "tiles_crumbled_S": [11,132,244,338],
  "tiles_decorated_S": [11,128,244,349],
  "tiles_steps_S": [11,154,244,349],
  "tree_S": [85,172,211,305],
  "trees_S": [23,174,220,308],
  "walls_broken_S": [57,213,175,317],
  "walls_corner_S": [12,194,243,349],
  "walls_end_S": [12,194,243,348],
  "walls_left_S": [105,194,243,303],
  "walls_right_S": [12,235,152,349],
  "walls_sides_S": [11,194,243,348],
  "walls_square_S": [11,194,243,349],
  "water_center_S": [12,128,244,348],
  "water_fall_S": [11,128,244,349],
};

// ── Préchargement ─────────────────────────────────────────────
// Si un PNG est absent, on le marque et on le saute silencieusement :
// une exception de drawImage() arrêtait toute la boucle de rendu.
const cache = new Map();
const failed = new Set();

export function loadTile(key) {
  if (cache.has(key)) return cache.get(key);
  const img = new Image();
  img.onerror = () => {
    failed.add(key);
    console.warn(`[oasis] tuile introuvable : ${TILE_FILES[key]}.png (clé "${key}")`);
  };
  img.src = `${BASE}/${TILE_FILES[key]}.png`;
  cache.set(key, img);
  return img;
}

// À appeler avant tout drawImage() : renvoie false si l'image est
// absente, cassée, ou pas encore chargée.
export function tileOk(img) {
  return !!img && img.complete && img.naturalWidth > 0;
}

export function preloadAll() {
  return Promise.all(
    Object.keys(TILE_FILES).map(
      (key) =>
        new Promise((resolve) => {
          const img = loadTile(key);
          if (img.complete) resolve();
          else { img.onload = resolve; img.onerror = resolve; }
        })
    )
  );
}

// ── Vignettes ─────────────────────────────────────────────────
// Une vignette montre le sujet recadré sur sa boîte englobante, à
// taille constante quelle que soit sa position dans l'image 256×352.
// C'est CE calcul qui manquait aux images uploadées par l'admin :
// elles étaient affichées entières en `object-fit: contain`, donc un
// sujet posé bas dans son gabarit apparaissait minuscule et collé en
// bas de la vignette. Les deux fonctions ci-dessous partagent
// exactement la même formule — Kenney et images perso sont cadrées à
// l'identique.

function boxStyle(url, bb, size) {
  const [x0, y0, x1, y1] = bb;
  const w = Math.max(1, x1 - x0), h = Math.max(1, y1 - y0);
  const scale = size / Math.max(w, h);
  return {
    width: size,
    height: size,
    backgroundImage: `url(${url})`,
    backgroundRepeat: "no-repeat",
    backgroundSize: `${IMG_W * scale}px ${IMG_H * scale}px`,
    backgroundPosition: `${-x0 * scale + (size - w * scale) / 2}px ${-y0 * scale + (size - h * scale) / 2}px`,
  };
}

// Style CSS pour afficher une tuile Kenney recadrée sur sa bbox
export function croppedStyle(tileKey, size) {
  const file = TILE_FILES[tileKey];
  const bb = TILE_BBOX[file];
  if (!bb) return { width: size, height: size };
  return boxStyle(`${BASE}/${file}.png`, bb, size);
}

// Même chose pour une image personnalisée (data URL) dont on connaît
// la boîte englobante, mesurée à l'upload (voir overrides.js).
export function croppedUrlStyle(url, bbox, size) {
  if (!url) return { width: size, height: size };
  const bb = bbox && bbox.length === 4 ? bbox : [0, 0, IMG_W, IMG_H];
  return boxStyle(url, bb, size);
}
